import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Award, CheckCircle, Briefcase, LineChart, Lightbulb, PenTool } from 'lucide-react';
const Home: React.FC = () => {
  return <div className="w-full" data-id="element-943">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 md:py-24" data-id="element-944">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-945">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-946">
            <div data-id="element-947">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-id="element-948">
                Discover Modern Career Paths with Your Education Degree
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-8" data-id="element-949">
                Your education degree opens doors to far more than teaching.
                Explore diverse, exciting career opportunities that value your
                unique skills and knowledge.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-950">
                <Link to="/courses" className="bg-white text-blue-700 hover:bg-blue-50 px-6 py-3 rounded-md font-semibold text-center" data-id="element-951">
                  Explore Career Paths
                </Link>
                <Link to="/signup" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-700 px-6 py-3 rounded-md font-semibold text-center transition-colors" data-id="element-952">
                  Sign Up Free
                </Link>
              </div>
            </div>
            <div className="hidden md:block" data-id="element-953">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/eK4XHAnUweGbcvDCboxbqY/ffffff.png" alt="Education students exploring career options" className="rounded-lg shadow-xl" width="600" height="400" data-id="element-954" />
            </div>
          </div>
        </div>
      </section>
      {/* Transferable Skills Section */}
      <section className="py-16 bg-white" data-id="element-955">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-956">
          <div className="text-center mb-16" data-id="element-957">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-958">
              Your Valuable Transferable Skills
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-959">
              Your education degree has equipped you with highly sought-after
              skills that employers across industries value.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-960">
            <div className="bg-blue-50 p-8 rounded-lg" data-id="element-961">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-962">
                <PenTool className="text-blue-600" size={24} data-id="element-963" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center" data-id="element-964">
                Communication Skills
              </h3>
              <p className="text-gray-600" data-id="element-965">
                Your ability to explain complex concepts clearly, adapt your
                communication style to different audiences, and create
                compelling presentations is invaluable in corporate settings.
              </p>
            </div>
            <div className="bg-blue-50 p-8 rounded-lg" data-id="element-966">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-967">
                <Users className="text-blue-600" size={24} data-id="element-968" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center" data-id="element-969">
                Leadership Abilities
              </h3>
              <p className="text-gray-600" data-id="element-970">
                Classroom management translates directly to team leadership.
                Your experience guiding groups, resolving conflicts, and
                creating collaborative environments is highly marketable.
              </p>
            </div>
            <div className="bg-blue-50 p-8 rounded-lg" data-id="element-971">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-972">
                <LineChart className="text-blue-600" size={24} data-id="element-973" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center" data-id="element-974">
                Analytical Thinking
              </h3>
              <p className="text-gray-600" data-id="element-975">
                Your experience with assessment, data analysis, and progress
                tracking provides you with analytical skills that are valuable
                in research, data analysis, and program evaluation roles.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Career Paths Section */}
      <section className="py-16 bg-gray-50" data-id="element-976">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-977">
          <div className="text-center mb-16" data-id="element-978">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-979">
              Discover Exciting Career Paths
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-980">
              Your education degree opens doors to diverse professional
              opportunities beyond traditional teaching roles.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-981">
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-duration-300" data-id="element-982">
              <div className="p-6" data-id="element-983">
                <Briefcase className="h-10 w-10 text-blue-600 mb-4" data-id="element-984" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-985">
                  Corporate Training & Development
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-986">
                  Apply your teaching expertise to develop and deliver training
                  programs that help employees build skills and advance their
                  careers.
                </p>
                <Link to="/courses/2" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-987">
                  Learn more <ArrowRight className="ml-2 h-4 w-4" data-id="element-988" />
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-duration-300" data-id="element-989">
              <div className="p-6" data-id="element-990">
                <Lightbulb className="h-10 w-10 text-blue-600 mb-4" data-id="element-991" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-992">
                  Instructional Design & eLearning
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-993">
                  Create engaging digital learning experiences by applying your
                  knowledge of effective teaching methods to online and blended
                  learning environments.
                </p>
                <Link to="/courses/3" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-994">
                  Learn more <ArrowRight className="ml-2 h-4 w-4" data-id="element-995" />
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-duration-300" data-id="element-996">
              <div className="p-6" data-id="element-997">
                <BookOpen className="h-10 w-10 text-blue-600 mb-4" data-id="element-998" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-999">
                  Educational Technology
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1000">
                  Join the rapidly growing edtech sector, where your educational
                  expertise gives you a competitive edge in developing and
                  implementing learning technologies.
                </p>
                <Link to="/courses/4" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-1001">
                  Learn more <ArrowRight className="ml-2 h-4 w-4" data-id="element-1002" />
                </Link>
              </div>
            </div>
          </div>
          <div className="text-center mt-10" data-id="element-1003">
            <Link to="/courses" className="inline-flex items-center px-6 py-3 border border-blue-600 text-blue-600 bg-white rounded-md hover:bg-blue-50 font-medium" data-id="element-1004">
              View All Career Paths <ArrowRight className="ml-2 h-4 w-4" data-id="element-1005" />
            </Link>
          </div>
        </div>
      </section>
      {/* How It Works Section */}
      <section className="py-16 bg-white" data-id="element-1006">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1007">
          <div className="text-center mb-16" data-id="element-1008">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1009">
              How Our Program Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-1010">
              We guide you through a comprehensive journey to discover and
              prepare for exciting alternative careers.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1011">
            <div className="bg-blue-50 p-6 rounded-lg text-center" data-id="element-1012">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1013">
                <CheckCircle className="text-blue-600" size={24} data-id="element-1014" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1015">
                Discover
              </h3>
              <p className="text-gray-600" data-id="element-1016">
                Explore diverse career paths and identify which opportunities
                align with your skills and interests.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg text-center" data-id="element-1017">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1018">
                <BookOpen className="text-blue-600" size={24} data-id="element-1019" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1020">
                Learn
              </h3>
              <p className="text-gray-600" data-id="element-1021">
                Develop the specific skills and knowledge needed to excel in
                your chosen alternative career path.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg text-center" data-id="element-1022">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1023">
                <Users className="text-blue-600" size={24} data-id="element-1024" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1025">
                Connect
              </h3>
              <p className="text-gray-600" data-id="element-1026">
                Join our community of education graduates who are successfully
                transitioning to alternative careers.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg text-center" data-id="element-1027">
              <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1028">
                <Award className="text-blue-600" size={24} data-id="element-1029" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1030">
                Transition
              </h3>
              <p className="text-gray-600" data-id="element-1031">
                Implement your personalized career transition plan with our
                guidance and resources.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50" data-id="element-1032">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1033">
          <div className="text-center mb-12" data-id="element-1034">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1035">
              Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-1036">
              Hear from education graduates who have successfully transitioned
              to exciting alternative careers.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-1037">
            {/* Testimonial 1 */}
            <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1038">
              <div className="flex items-center mb-4" data-id="element-1039">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-1040">
                  <span className="text-blue-600 font-bold" data-id="element-1041">NG</span>
                </div>
                <div data-id="element-1042">
                  <h4 className="font-semibold text-gray-800" data-id="element-1043">Ngozi Adeyemi</h4>
                  <p className="text-sm text-gray-600" data-id="element-1044">
                    Education to Instructional Designer
                  </p>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-1045">
                "I was passionate about teaching but wanted to explore other
                options. This program helped me discover instructional design,
                where I now create engaging learning experiences for a major
                corporation. I use my teaching skills every day, just in a new
                context!"
              </p>
            </div>
            {/* Testimonial 2 */}
            <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1046">
              <div className="flex items-center mb-4" data-id="element-1047">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-1048">
                  <span className="text-blue-600 font-bold" data-id="element-1049">KM</span>
                </div>
                <div data-id="element-1050">
                  <h4 className="font-semibold text-gray-800" data-id="element-1051">Kwame Mensah</h4>
                  <p className="text-sm text-gray-600" data-id="element-1052">
                    Education to L&D Manager
                  </p>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-1053">
                "The course showed me how to translate my classroom experience
                into corporate language. Now I lead learning and development for
                a tech company, helping employees grow their skills. My
                education background gives me a unique perspective my colleagues
                value."
              </p>
            </div>
            {/* Testimonial 3 */}
            <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1054">
              <div className="flex items-center mb-4" data-id="element-1055">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-1056">
                  <span className="text-blue-600 font-bold" data-id="element-1057">FN</span>
                </div>
                <div data-id="element-1058">
                  <h4 className="font-semibold text-gray-800" data-id="element-1059">Fatima Nkosi</h4>
                  <p className="text-sm text-gray-600" data-id="element-1060">
                    Education to EdTech Specialist
                  </p>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-1061">
                "I never imagined my education degree would lead to a career in
                technology! Now I work with developers to create educational
                apps that impact thousands of students. This program helped me
                see possibilities I hadn't considered before."
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white" data-id="element-1062">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1063">
          <h2 className="text-3xl font-bold mb-6" data-id="element-1064">
            Ready to Explore New Career Possibilities?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto" data-id="element-1065">
            Your education degree is more valuable than you think. Discover the
            diverse career paths waiting for you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1066">
            <Link to="/signup" className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 rounded-md font-semibold" data-id="element-1067">
              Start Your Journey
            </Link>
            <Link to="/courses" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-700 px-8 py-3 rounded-md font-semibold transition-colors" data-id="element-1068">
              Explore Career Paths
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;